// @flow

import * as React from 'react';

import { useToggle } from 'reshaped';

import { selectCanChooseResellee } from '@gandi/cart';
import { selectOneOrganization } from '@gandi/redux-organization';

import type { Contact, Role } from 'src/Cart/ContactForm/types';
import { selectCustomersList } from 'src/Cart/Customer/modules/customers';
import { useModalType } from 'src/Reshaped/hooks/useModalType';
import type {
  ModalType,
  OpenModalFunction,
  OpenModalParams,
} from 'src/Reshaped/hooks/useModalType';
import { useAppSelector } from 'src/hooks/useAppSelector';

import { useCartSummaryContext } from '../CartSummary';

type ModalEntry = {
  type: ModalType,
  orgId?: string,
  actionOnSuccess?: 'onClose' | 'onBack',
};

type ModalContextType = {
  active: boolean,
  modalContent: React.Node,
  modalTitle: React.Node,
  onClose: () => void,
  openModal: OpenModalFunction,
  onAfterClose: () => void,
};

const ModalContext: React.Context<ModalContextType> = React.createContext({
  active: false,
  modalContent: null,
  modalTitle: '',
  onClose: () => {},
  openModal: () => {},
  onAfterClose: () => {},
});

export const useModalContext = (): ModalContextType =>
  React.useContext(ModalContext);

type Props = {
  children: React.MixedElement,
};

const defaultRole = 'BILLING';

export const ModalProvider = ({ children }: Props): React.MixedElement => {
  const [stack, setStack] = React.useState<ModalEntry[]>([]);
  const [currentRadioValue, setCurrentRadioValue] = React.useState<?string>();
  const [role, setRole] = React.useState<Role>(defaultRole);

  const canChooseResellee = useAppSelector(selectCanChooseResellee);

  const { productOwnerOrganization } = useCartSummaryContext();

  const productOwnerIsResellee =
    productOwnerOrganization &&
    'entity_type' in productOwnerOrganization &&
    productOwnerOrganization.entity_type === 'resellee';

  const defaultTab =
    canChooseResellee && (productOwnerIsResellee || !productOwnerOrganization)
      ? 'customers'
      : 'organizations';

  const [userSelectedTabs, setUserSelectedTab] = React.useState<
    'customers' | 'organizations' | null,
  >(null);

  const tabs = userSelectedTabs ?? defaultTab;

  const [lastOrg, setLastOrg] = React.useState(productOwnerOrganization);
  if (productOwnerOrganization !== lastOrg) {
    setLastOrg(productOwnerOrganization);
    setUserSelectedTab(null);
  }
  const { active, activate, deactivate } = useToggle(false);

  const current = stack[stack.length - 1];
  const { modalTitle, Modal } = useModalType(current?.type);

  const contact = useAppSelector(state => {
    if (!current?.orgId) return undefined;

    if (role === 'CUSTOMER') {
      const customers = selectCustomersList(state);
      return customers.find(cust => cust.id === current.orgId);
    }

    return selectOneOrganization(state, current.orgId);
  });

  const contactRef = React.useRef<Contact>(contact);
  contactRef.current = contact;

  // Ensure single call to `deactivate`
  const onClose = React.useCallback(() => {
    if (active) {
      deactivate();
    }
  }, [active, deactivate]);

  const onAfterClose = React.useCallback(() => {
    setStack([current]);
    // Reset radioValue
    setCurrentRadioValue();
    // Reset role
    setRole(defaultRole);
  }, [current]);

  const openModal = React.useCallback(
    ({
      type,
      orgId,
      replace,
      actionOnSuccess,
      currentRole,
    }: OpenModalParams) => {
      if (replace) setStack([{ type, orgId, actionOnSuccess }]);
      else setStack(prev => [...prev, { type, orgId, actionOnSuccess }]);

      if (currentRole) setRole(currentRole);
      activate();
    },
    [activate],
  );

  const onBack = React.useCallback(() => {
    setStack(prev => (prev.length > 1 ? prev.slice(0, -1) : prev));
  }, []);

  const modalContentElement = React.useMemo(() => {
    if (!Modal) return null;
    return (
      <Modal
        onClose={onClose}
        onBack={stack.length > 1 ? onBack : undefined}
        openModal={openModal}
        contact={contactRef.current}
        tabs={tabs}
        setTabs={setUserSelectedTab}
        actionOnSuccess={current?.actionOnSuccess}
        currentRadioValue={currentRadioValue}
        setCurrentRadioValue={setCurrentRadioValue}
        role={role}
      />
    );
  }, [
    Modal,
    current?.actionOnSuccess,
    currentRadioValue,
    onBack,
    onClose,
    openModal,
    role,
    stack.length,
    tabs,
  ]);

  const contextValue = React.useMemo(
    () => ({
      active,
      onClose,
      modalContent: modalContentElement,
      modalTitle,
      openModal,
      onAfterClose,
    }),
    [active, onClose, onAfterClose, modalContentElement, modalTitle, openModal],
  );

  return (
    <ModalContext.Provider value={contextValue}>
      {children}
    </ModalContext.Provider>
  );
};
